import { useEffect } from "react";
import { api } from "../lib/api";
import { pendingWalkRoundId } from "../lib/balePending";

/**
 * useBaleSession — bale-session live sync for the scoring page.
 *
 * Mechanical extraction of the bale-session machinery from Score.jsx:
 *   • fetch the bale session when the round joins one
 *   • initial bale redirect (start at the right archer on load)
 *   • solo detection (skip SSE/poll for the single-archer case)
 *   • SSE push + 10s fallback poll, with auto-follow/auto-navigate and live
 *     draft sync
 *   • broadcast the scorer's current view to participants
 *   • solo→multi transition-detection interval
 *
 * Behavior is preserved verbatim — every concurrency guard (liveRef closures,
 * localChangeRef suppression, content-equality ping-pong guard, isSolo
 * re-subscribe) is load-bearing and unchanged. The `baleSession` state is owned
 * by useScoringSession (it is mutated by confirmEnd/nextArcher/skip too) and
 * passed in; this hook returns the derived `isSolo`.
 *
 * #2061 — the control refs arrive as ONE session-owned `refs` bundle rather than
 * as individual caller-owned refs.
 */
export function useBaleSession({
  round,
  baleSession,
  setBaleSession,
  user,
  isScorer,
  navigate,
  refs,
  setCurrent,
  setLastSaved,
  setRound,
}) {
  const { liveRef, localChangeRef, initialBaleRedirectDone } = refs;

  // ─── fetch bale session when round is in a bale session ───
  useEffect(() => {
    if (!round?.bale_session_id) return;
    api.getBaleSession(round.bale_session_id)
      .then(s => { setBaleSession(s); })
      .catch(() => {});
  }, [round?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── initial bale redirect — ensure we start at the right archer ───
  // When opening the scoring page, if an archer in a lower bale position that
  // THIS DEVICE SCORES FOR hasn't scored the current end yet, navigate to
  // their round first. This mirrors the post-confirmEnd redirect but runs
  // once on initial load.
  //
  // #1898 round 3 (QA FAIL 1): the target comes from the shared scores-for
  // walk helper — the old unfiltered first-pending pick bounced a registered
  // invitee off their own round onto the HOST's round ~40ms after landing,
  // and their keypad taps drafted arrows into the host's card. When the only
  // pending archers are registered mates (scoring on their own devices), the
  // helper returns null and the device stays on the user's own round. Own
  // archer is included on purpose: when we sort first, no navigation happens.
  useEffect(() => {
    if (!baleSession || !round || !round.bale_session_id) return;
    if (initialBaleRedirectDone.current) return;
    initialBaleRedirectDone.current = true;

    const walkRoundId = pendingWalkRoundId(baleSession.archers, {
      endIdx: round.ends_done,
      arrowsPerEnd: round.arrows_per_end,
      currentRoundId: round.id,
      // No rotation and no self-exclusion: on mount we walk from the TOP of
      // the bale order, and our own round is a valid stop.
      isScorer,
    });
    if (walkRoundId) {
      navigate('/score?round=' + walkRoundId);
    }
  }, [baleSession, round]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── solo mode — true when this bale session has at most one registered user ───
  // When solo, skip the SSE subscription and fallback poll to avoid a persistent
  // server connection for the most common single-archer use case. Derived (not
  // state) so it recomputes on every render as baleSession updates.
  const isSolo = Boolean(
    round?.bale_session_id &&
    baleSession &&
    (baleSession.archers || []).filter(a => a.is_registered).length <= 1
  );

  // ─── SSE push + 10s fallback poll for bale session live sync ───
  // When Archer A confirms the bale session end on behalf of everyone, Archer B's
  // device needs to detect the advancement within ~100-500ms instead of waiting 5s.
  // SSE is the fast path; the 10s setInterval fallback covers SSE disconnections.
  // We compare the bale session's ends_completed for our archer against the
  // local round's ends_done — if the session is ahead, reload the round.
  // Solo mode: SSE subscription and fallback poll are skipped — re-subscribe
  // automatically when isSolo flips to false (second registered user joins).
  useEffect(() => {
    if (!round?.bale_session_id) return;

    // Solo mode: skip SSE subscription and fallback polling.
    // Re-subscribe happens automatically when isSolo flips to false.
    if (isSolo) return;

    async function handleBaleUpdate() {
      try {
        const session = await api.getBaleSession(round.bale_session_id);

        // Read fresh state via liveRef (baleSession captured in closure is stale)
        const { round: liveRound, baleSession: prevSession, user: liveUser, roundParam: liveRoundParam } = liveRef.current;

        // Auto-follow: if viewer was on the scorer's current round and the scorer
        // changed their view (either navigated to a different round, or confirmed
        // an end so next_pending_round_id advanced), navigate with them.
        const targetRoundId = session.scorer_current_round_id ?? session.next_pending_round_id;
        const prevTargetRoundId = prevSession?.scorer_current_round_id ?? prevSession?.next_pending_round_id;
        // Use roundParam (URL) as the viewer's intent — it updates immediately on
        // navigation whereas liveRound.id lags while the new round is loading.
        const liveViewingRoundId = liveRoundParam ? Number(liveRoundParam) : liveRound?.id;
        if (
          prevSession?.scorer_user_id != null &&                          // scorer mode is active
          liveUser?.id !== prevSession.scorer_user_id &&                  // current user is a viewer
          liveViewingRoundId === prevTargetRoundId &&                     // viewer's URL was on scorer's round
          targetRoundId != null &&
          targetRoundId !== prevTargetRoundId                             // scorer changed their view
        ) {
          navigate('/score?round=' + targetRoundId);
          // #1285 — the scorer advanced to a *different* archer's round. Clear the
          // previous archer's dots immediately so they don't linger on screen until
          // the async round-load effect (keyed on [loadedRound, loading]) finishes.
          // The viewer is read-only, so dropping local `current` is safe. Update
          // baleSession and early-return: the round-load effect loads the NEW round's
          // draft, so we must NOT fall through to the draft-sync block below which
          // still keys off the STALE pre-navigate `liveRound.archer_id` and would
          // otherwise re-apply the old archer's draft into the just-cleared `current`.
          setCurrent({ arrows: [], positions: [] });
          setLastSaved(null);
          setBaleSession(session);
          return;
        }

        // Auto-navigate when scorer mode is first enabled: bring viewer to the scorer's round
        if (
          prevSession?.scorer_user_id == null &&           // scorer mode was OFF before
          session.scorer_user_id != null &&                // scorer mode is ON now
          liveUser?.id !== session.scorer_user_id &&       // current user is not the scorer
          session.next_pending_round_id != null &&
          liveRound?.id !== session.next_pending_round_id  // not already on the scorer's round
        ) {
          navigate('/score?round=' + session.next_pending_round_id);
        }

        // Auto-navigate when scorer mode is deactivated: return viewer to their own round
        if (
          prevSession?.scorer_user_id != null &&  // scorer mode WAS active
          session.scorer_user_id == null &&       // scorer mode is now OFF
          liveUser?.id != null
        ) {
          const mySlot = session.archers.find(a => a.user_id === liveUser.id);
          if (mySlot && mySlot.round_id && mySlot.round_id !== liveRound?.id) {
            navigate('/score?round=' + mySlot.round_id);
          }
        }

        setBaleSession(session);
        if (!liveRound) return;

        // Live draft sync: reload when the server draft has more arrows than local
        // (new arrow added) OR when another user was the last to save (handles
        // repositions, which don't change the arrow count).
        // Content-equality check prevents ping-pong: Device B syncs, autosaves, changes
        // the saver — Device A re-syncs but sees identical content and skips setCurrent,
        // so no autosave fires and the loop stops.
        const viewingEntry = session.archers.find(a => a.archer_id === liveRound.archer_id);
        const myUserId = user?.id;
        if (
          viewingEntry &&
          !localChangeRef.current &&
          viewingEntry.current_draft_arrows > 0 &&
          (viewingEntry.current_draft_arrows > liveRef.current.current.arrows.length ||
           (viewingEntry.draft_last_saver_id != null &&
            viewingEntry.draft_last_saver_id !== myUserId))
        ) {
          api.round(liveRound.id).then(freshRound => {
            if (!freshRound?.draft?.arrows?.length) return;
            const newScores = freshRound.draft.arrows.map(a => a.score);
            const newPositions = freshRound.draft.arrows.map(a => ({ x: a.x, y: a.y }));
            const localArrows = liveRef.current.current.arrows;
            const localPositions = liveRef.current.current.positions;
            const scoresChanged = newScores.length !== localArrows.length ||
              newScores.some((s, i) => s !== localArrows[i]);
            const positionsChanged = newPositions.some((p, i) =>
              p.x !== localPositions[i]?.x || p.y !== localPositions[i]?.y);
            if (scoresChanged || positionsChanged) {
              setCurrent({ arrows: newScores, positions: newPositions });
            }
          }).catch(() => {});
        }

        const myEntry = session.archers.find(a => a.archer_id === liveRound.archer_id);
        // If the bale session shows more ends completed than our local round knows
        // about, reload the round so the end counter advances automatically.
        if (myEntry && myEntry.ends_completed > liveRound.ends_done) {
          const fresh = await api.round(liveRound.id);
          setRound(fresh);
          // End confirmed by another device — always clear current-end state.
          // Any arrows in current belong to the just-confirmed end; carrying
          // them into the new end would autosave them as a stale draft.
          setCurrent({ arrows: [], positions: [] });
          setLastSaved(null);
          // Animations are triggered by the arrival-based useEffect when
          // currentEndNumber changes after setRound(fresh).
        }
      } catch {}
    }

    const es = new EventSource(
      api.baleSessionStreamUrl(round.bale_session_id),
      { withCredentials: true }
    );
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'update') handleBaleUpdate();
      } catch {}
    };
    es.onerror = () => {}; // fallback poll handles reconnect gaps

    const fallbackId = setInterval(handleBaleUpdate, 10000);

    return () => {
      es.close();
      clearInterval(fallbackId);
    };
  }, [round?.bale_session_id, isSolo]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── broadcast scorer's current view to all participants via SSE ───
  // When the scorer navigates to a different round, tell the server so viewers
  // can follow. Fire-and-forget — errors are ignored (non-fatal).
  useEffect(() => {
    if (!isScorer || !round?.bale_session_id || !round?.id) return;
    api.patchScorerView(round.bale_session_id, round.id).catch(() => {});
  }, [round?.id, isScorer]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── transition-detection interval (solo → multi) ───
  // While solo, poll every 15s to detect when a second registered user joins.
  // When isSolo flips to false, this effect's cleanup clears the interval and
  // the SSE effect above re-runs to open the EventSource.
  useEffect(() => {
    if (!round?.bale_session_id || !isSolo) return;

    const transitionId = setInterval(async () => {
      try {
        const session = await api.getBaleSession(round.bale_session_id);
        setBaleSession(session);
        // isSolo recomputes — if a second user joined it flips to false,
        // this effect re-runs with isSolo=false and the interval is cleared,
        // and the SSE effect re-runs opening the EventSource.
      } catch {}
    }, 15000);

    return () => clearInterval(transitionId);
  }, [round?.bale_session_id, isSolo]); // eslint-disable-line react-hooks/exhaustive-deps

  return { isSolo };
}
